/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { OnboardClinicSchema, type OnboardClinicInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, useState, type JSX } from 'react';
import {
  type FieldErrors,
  type FieldPath,
  type UseFormRegister,
  useForm,
  type UseFormTrigger,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useOnboardClinic } from '../../lib/queries/sa.js';

interface WizardProps {
  readonly onDone: () => void;
}

type Step = 0 | 1 | 2;

function numberOrUndefined(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

interface FieldsProps {
  readonly register: UseFormRegister<OnboardClinicInput>;
  readonly errors: FieldErrors<OnboardClinicInput>;
}

/** Step 1 — clinic identity + contact. */
function ClinicStep({ register, errors }: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <FormField
        id="ob-name"
        type="text"
        label={t('sa.wizard.clinicName')}
        autoComplete="off"
        error={errors.clinic?.name?.message}
        registration={register('clinic.name')}
      />
      <FormField
        id="ob-legalName"
        type="text"
        label={t('sa.wizard.legalName')}
        autoComplete="off"
        error={errors.clinic?.legalName?.message}
        registration={register('clinic.legalName')}
      />
      <FormField
        id="ob-contactEmail"
        type="email"
        label={t('sa.wizard.contactEmail')}
        autoComplete="off"
        error={errors.clinic?.contactEmail?.message}
        registration={register('clinic.contactEmail')}
      />
      <FormField
        id="ob-contactPhone"
        type="tel"
        label={t('sa.wizard.contactPhone')}
        autoComplete="off"
        error={errors.clinic?.contactPhone?.message}
        registration={register('clinic.contactPhone')}
      />
    </>
  );
}

type AddressField = 'line1' | 'city' | 'state' | 'postalCode' | 'country';
const ADDRESS_FIELDS: readonly AddressField[] = ['line1', 'city', 'state', 'postalCode', 'country'];

/** Step 2 — registered address. */
function AddressStep({ register, errors }: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  const addr = errors.clinic?.address;
  return (
    <>
      {ADDRESS_FIELDS.map((field) => (
        <FormField
          key={field}
          id={`ob-${field}`}
          type="text"
          label={t(`sa.wizard.${field}`)}
          autoComplete="off"
          error={addr?.[field]?.message}
          registration={register(`clinic.address.${field}`)}
        />
      ))}
    </>
  );
}

/** Step 3 — first Clinic Admin + plan. */
function AdminStep({ register, errors }: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <FormField
        id="ob-adminName"
        type="text"
        label={t('sa.wizard.adminName')}
        autoComplete="off"
        error={errors.firstAdmin?.fullName?.message}
        registration={register('firstAdmin.fullName')}
      />
      <FormField
        id="ob-adminEmail"
        type="email"
        label={t('sa.wizard.adminEmail')}
        autoComplete="off"
        error={errors.firstAdmin?.email?.message}
        registration={register('firstAdmin.email')}
      />
      <FormField
        id="ob-planSlug"
        type="text"
        label={t('sa.wizard.planSlug')}
        autoComplete="off"
        error={errors.planSlug?.message}
        registration={register('planSlug')}
      />
      <FormField
        id="ob-trialDays"
        type="number"
        label={t('sa.wizard.trialDays')}
        autoComplete="off"
        error={errors.trialDays?.message}
        registration={register('trialDays', { setValueAs: numberOrUndefined })}
      />
    </>
  );
}

// Validate only the leaf fields visible on each step — triggering a parent
// object (e.g. `clinic`) would also validate the still-empty address fields
// that belong to a later step, blocking Next on hidden inputs.
const STEP_FIELDS: Readonly<Record<Step, readonly FieldPath<OnboardClinicInput>[]>> = {
  0: ['clinic.name', 'clinic.legalName', 'clinic.contactEmail', 'clinic.contactPhone'],
  1: [
    'clinic.address.line1',
    'clinic.address.city',
    'clinic.address.state',
    'clinic.address.postalCode',
    'clinic.address.country',
  ],
  2: ['firstAdmin.fullName', 'firstAdmin.email', 'planSlug', 'trialDays'],
};

function useStepNav(
  step: Step,
  setStep: (s: Step) => void,
  trigger: UseFormTrigger<OnboardClinicInput>,
): { readonly next: () => void; readonly back: () => void } {
  const next = useCallback(() => {
    void (async (): Promise<void> => {
      const ok = await trigger(STEP_FIELDS[step]);
      if (ok && step < 2) {
        setStep((step + 1) as Step);
      }
    })();
  }, [step, setStep, trigger]);
  const back = useCallback(() => {
    if (step > 0) {
      setStep((step - 1) as Step);
    }
  }, [step, setStep]);
  return { next, back };
}

/** Multi-step "New Clinic" onboarding wizard (validated by `OnboardClinicSchema`). */
export function OnboardingWizard({ onDone }: WizardProps): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(0);
  const onboard = useOnboardClinic();
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<OnboardClinicInput>({ resolver: zodResolver(OnboardClinicSchema) });
  const { next, back } = useStepNav(step, setStep, trigger);

  const submit = useCallback(
    async (values: OnboardClinicInput): Promise<void> => {
      await onboard.mutateAsync(values);
      onDone();
    },
    [onboard, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {t('sa.wizard.stepOf', { step: step + 1, total: 3 })}
      </p>
      {step === 0 && <ClinicStep register={register} errors={errors} />}
      {step === 1 && <AddressStep register={register} errors={errors} />}
      {step === 2 && <AdminStep register={register} errors={errors} />}

      {onboard.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-between gap-3 pt-2">
        <Button type="button" variant="outline" onClick={back} disabled={step === 0}>
          {t('sa.wizard.back')}
        </Button>
        {step < 2 ? (
          <Button type="button" onClick={next}>
            {t('sa.wizard.next')}
          </Button>
        ) : (
          <Button type="submit" disabled={isSubmitting || onboard.isPending}>
            {t('sa.wizard.submit')}
          </Button>
        )}
      </div>
    </form>
  );
}
